import { useCallback } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import { useAtomValue } from 'jotai';
import mixpanel from 'mixpanel-browser';
import type { RootStateOrAny } from 'react-redux';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import type { ValueOf } from 'type-fest';
import { Userpilot } from 'userpilot';

import { selectDateAtom } from '@/modules/patientList/atoms';

import {
  selectedUnifiedIdAtom,
  selectedVisitAtom,
} from '../app/appHeader/imageViewerMenu/atoms';
import config from '../config';
import { casePresentationModeAtom } from '../modules/annotator/casePresentation/atoms';
import { confidenceScoreFilterAtom } from '../modules/annotator/findingSidebar/confidenceScoreFilter/atoms';
import { showSegmentationAtom } from '../modules/annotator/multiFileViewer/imageToolbar/atoms';
import { isMagicLinkUserAtom } from '../modules/login/atoms';
import { getAppName } from '../modules/login/login-utils';
import { useShouldHideHuddle } from '../modules/patientList/useShouldHideHuddle';
import { makeAPIRequestReactQuery } from '../react-query/api';
import { endpoints } from '../react-query/endpoints';
import { useEvents } from '../react-query/useEvents';
import { usePreferences } from '../react-query/usePreferences';

import { getExternalJwtToken } from './api';
import type { Source } from './cornerstone/types';
import type { CornerstoneImage } from './cornerstone/useCornerstone';
import { useIsEmbedded } from './hooks';
import { sendException } from './sentry';

mixpanel.init(config.MIXPANEL_TOKEN, {
  record_sessions_percent: 1,
  record_heatmap_data: true,
});
Userpilot.initialize(config.USERPILOT_TOKEN);

export const mixpanelEvents = {
  //--- Uncategorized
  analyticsView: 'user.view.analytics',
  analyticsViewDuration: 'user.view.analytics.duration',
  autoTrialLandOnPage: 'user.auto.trial.land.on.page',
  autoTrialSchedule: 'user.click.auto.trial.schedule',
  autoTrialVideo: 'user.auto.trial.play.video',
  autoTrialWebinar: 'user.click.auto.trial.webinar',
  casePresentationAnnotate: 'user.case.presentation.annotate',
  casePresentationHideFinding: 'user.case.presentation.hide.finding',
  casePresentationSelectFinding: 'user.case.presentation.select.finding',
  casePresentationSideBySide: 'user.case.presentation.side.by.side',
  casePresentationSuggestion: 'user.case.presentation.suggestion',
  casePresentationSuggestionPreview:
    'user.case.presentation.suggestion.preview',
  casePresentationView: 'user.case.presentation.view',
  casePresentationViewDuration: 'user.case.presentation.view.duration',
  toggleAttachmentPrint: 'user.toggle.attachment.print',
  downloadAttachmentPrint: 'user.download.attachment.print',
  editAttachmentServices: 'user.edit.attachment.services',
  saveAttachmentServices: 'user.save.attachment.services',
  clickDialogPrint: 'user.click.dialog.print',
  clickGroupByProvider: 'user.click.group.by.provider',
  compareVisits: 'user.compare.visits.click',
  reviewModeClick: 'user.review.mode.click',
  compareViewDuration: 'user.compare.view.duration',
  compareViewExit: 'user.compare.view.exit',
  compareVisitSelect: 'user.compare.visit.select',
  compareVisitChange: 'user.compare.visit.change',
  compareImageLoad: 'user.compare.image.load',
  compareImageLoadError: 'user.compare.image.load.error',
  compareImageFilterChange: 'user.compare.image.filter.change',
  compareCarouselPageChange: 'user.compare.carousel.page.change',
  compareImageNavigate: 'user.compare.image.navigate',
  compareImageSelect: 'user.compare.image.select',
  compareToothSelect: 'user.compare.tooth.select',
  confidenceThresholdChanged: 'user.confidenceThreshold.change',
  createAiValidation: 'create.ai_validation',
  createAgreementReason: 'create.agreement_reasons',
  createDisagreeReason: 'create.disagree_reasons',
  customerSupportInformationCopy: 'customer.support.information.copy',
  customerSupportWidgetOpen: 'customer.support.widget.open',
  dragAndDrop: 'user.image.drag.and.drop',
  embeddedPatientView: 'user.embedded.patient.view.click',
  hqToggle: 'user.hq.toggle',
  huddleIndicatorClick: 'huddle.indicator.click',
  huddleIndicatorGuideClick: 'huddle.indicator.guide.click',
  huddleIndicatorHover: 'huddle.indicator.tooltip.hover',
  huddleIndicatorLanguageToggleTx: 'huddle.indicator.language.toggle.tx',
  huddleIndicatorPreferenceToggle: 'huddle.indicator.preference.toggle',
  huddleIndicatorView: 'huddle.indicator.view',
  huddleLegendClick: 'huddle.legend.click',
  huddleNameSearchDropdownClick: 'user.huddle.name.search.dropdown.click',
  huddleNameSearchInput: 'user.huddle.name.search.input',
  huddlePotentialActionsSortClick: 'huddle.potential.actions.sort.click',
  huddlePrint: 'user.huddle.print',
  huddleRecentVisitsClick: 'user.huddle.recent.visits.click',
  huddleSelectDate: 'user.huddle.select.date',
  referralsFilterChanged: 'user.referrals.filter.changed',
  huddleSelectTreatmentFilter: 'user.huddle.select.treatment.filter',
  huddleSheetNextClick: 'huddle.sheet.next.click',
  huddleSheetPreviousClick: 'huddle.sheet.previous.click',
  huddleSort: 'user.huddle.sort',
  huddleTaskIndicatorFilterClick: 'user.huddle.task.indicator.filter.click',
  huddleTreatmentDetailsClick: 'huddle.treatment.details.click',
  hygenistToggle: 'user.huddle.hygenist.toggle',
  imageLoadTime: 'image.load.time',
  insurancePdf: 'user.patient.create.insurance.pdf',
  insuranceSummary: 'user.patient.create.insurance.summary',
  insuranceSummaryGenerate: 'user.patient.generate.insurance.summary',
  insuranceTreatmentsModal: 'user.patient.open.insurance.treatments.modal',
  latestImagesResponseTime: 'latest.images.response.time',
  login: 'user.login',
  logout: 'user.logout',
  miniOdontogramToothSelection: 'user.mini.odontogram.tooth.selection',
  narrativeGenerated: 'insurance.narrative.generated',
  newBarCheckForAnalyses: 'user.new.bar.check.for.analyses',
  newBarJumpToLatest: 'user.new.bar.jump.to.latest',
  newBarNewAnalyses: 'user.new.bar.new.analyses',
  odontogramHoverMouthPart: 'user.odontogram.hover.mouth.part',
  odontogramView: 'user.odontogram.view',
  oktaTokenManagerError: 'okta.token.manager.error',
  oktaAuthTokenRenewed: 'okta.auth.token.renewed',
  oktaAuthTokenExpired: 'okta.auth.token.expired',
  onboardingCompleted: 'user.onboarding.completed',
  onboardingStarted: 'user.onboarding.started',
  openShortcuts: 'user.open.shortcuts',
  patientAnalysisTime: 'user.patient.analysis.time',
  patientViewToggle: 'user.patient.view.toggle',
  planningReviewTab: 'user.planning.review.click',
  practiceConfidenceThresholdChanged:
    'user.practice.confidence.threshold.changed',
  presentSingleFinding: 'case.presentation.single.finding.click',
  privacyToggle: 'user.privacy.toggle',
  productUpdates: 'user.product.updates',
  referralTabDuration: 'user.referral.tab.duration',
  segmentationViewToggle: 'user.segmentation.view.toggle',
  selectOperatory: 'user.select.operatory',
  selectProvider: 'user.select.provider',
  settingsUpdate: 'user.settings.update',
  switchVisits: 'user.switch.visits',
  trackSizeFilterChange: 'user.caries.size.filter.change',
  toggleFindings: 'user.toggle.findings',
  toggleSidebar: 'user.toggle.sidebar.expanded',
  updateAiValidation: 'update.ai_validation',
  deleteUserAnnotation: 'delete.user_annotation',
  updateAgreementReason: 'update.agreement_reasons',
  updateDisagreeReason: 'update.disagree_reasons',
  userChangeActionableView: 'user.change.actionable.view',
  userChangeSpotlightPreference: 'user.change.spotlight.preference',
  userFinding: 'user.custom.finding',
  userHoverScheduledTodayPill: 'user.hover.scheduled.today.pill',
  userHoverRblPill: 'user.hover.rbl.pill',
  userMiniOdontogramHover: 'user.mini.odontogram.hover',
  userTaskInteraction: 'user.task.interaction',
  viewInsights: 'insights.view',
  viewPatient: 'user.patient.view',
  visitBinThresholdChanged: 'user.visit.bin.threshold.changed',
  visitConfidenceThresholdChanged: 'user.visit.confidence.threshold.changed',
  visitConfidenceThresholdToggled: 'user.visit.confidence.threshold.toggled',
  userViewAllClick: 'user.view.all.click',
  referralCompleted: 'user.referral.completed',
  referralCancelled: 'user.referral.cancelled',
  userShowOnlyFindingClick: 'user.show.only.finding.click',
  userShowOnlyFindingClear: 'user.show.only.finding.clear',
  //--- External
  externalPmsEntry: 'external.pms.entry',
  externalUrlBackToPatientList: 'user.external.url.back.to.patient.list',
  externalUrlSuccess: 'external.url.success',
  externalUrlTimeout: 'external.url.timeout',
  externalUpgradeClick: 'external.upgrade.click',
  externalError: 'external.error',
  externalErrorRefreshClick: 'external.error.refresh.click',
  externalTrialOfferClick: 'external.trial.offer.click',
  externalTrialOfferLearnMore: 'external.trial.offer.learn_more',
  externalCollapsibleToggle: 'external.collapsible.toggle',
  externalUpgradeModalOpen: 'external.upgrade.modal.open',
  externalAiImpactModalOpen: 'external.ai.impact.modal.open',
  externalOnboardingModalOpen: 'external.onboarding.modal.open',
  externalOnboardingBannerClose: 'external.onboarding.banner.close',
  externalOnboardingVideoPlay: 'external.onboarding.video.play',
  externalOnboardingVideoReportWatched:
    'external.onboarding.video.report.watched',
  externalCancelInitiate: 'external.cancel.initiate',
  externalManageSubscriptionOpen: 'external.manage.subscription.open',
  externalManageSubscriptionClose: 'external.manage.subscription.close',
  externalCancelGoBack: 'external.cancel.go.back',
  externalCancelClick: 'external.cancel.click',
  externalCancelReasonSelect: 'external.cancel.reason.select',
  externalTermsAndConditionsClick: 'external.terms.and.conditions.click',
  externalImageSelected: 'external.image.selected',
  externalContentLoaded: 'external.content.loaded',
  externalModalDuration: 'external.modal.duration',
  externalPatientListClick: 'external.patient.list.click',
  externalAiImpactTooltipOpen: 'external.ai.impact.tooltip.open',
  externalAiImpactRating: 'external.ai.impact.rating',
  externalAiImpactFeedback: 'external.ai.impact.feedback',
  //--- User Image
  rotateImageClick: 'user.image.rotate',
  rerunAnalysis: 'user.image.rerunAnalysis',
  rerunNewCariesModel: 'user.image.rerunNewCariesModel',
  viewImageDuration: 'user.image.view.duration',
  analyzeImage: 'user.image.analyze',
  hasFindings: 'user.image.has_findings',
  imageContrastChanged: 'user.image.contrast.changed',
  imageBrightnessChanged: 'user.image.brightness.changed',
  imageInversionChange: 'user.image.inversion.click',
  uploadImage: 'user.image.upload',
  viewImage: 'user.image.view',
  viewImageThumbnail: 'user.image.view.thumbnail',
  imageControlsReset: 'user.image.controls.reset',
  brightnessSliderToggle: 'user.image.brightness.slider.toggle',
  brightnessAdjusted: 'user.image.brightness.adjusted',
  contrastSliderToggle: 'user.image.contrast.slider.toggle',
  contrastAdjusted: 'user.image.contrast.adjusted',
  opacityInverted: 'user.image.opacity.inverted',
  magnifyingModeToggle: 'user.image.magnifying.mode.toggle',
  imageRotated: 'user.image.rotated',
  imageMirrored: 'user.image.mirrored',
  clickCreateFindingButton: 'user.click.create.finding.button',
  clickFullScreenButton: 'user.click.full.screen.button',
  // Image Footer Events
  imageFooterExpand: 'user.image.footer.expand',
  imageFooterCollapse: 'user.image.footer.collapse',
  imageTypeFilterSelect: 'user.image.type.filter.select',
  imageGridFilterSelect: 'user.image.grid.filter.select',
  // Visit Sidebar
  visitSidebarToothDamageToggle: 'user.visit.sidebar.tooth.damage.toggle',
  visitSidebarBoneLevelToggle: 'user.visit.sidebar.bone.level.toggle',
  visitSidebarBoneLevelAccordionToggle:
    'user.visit.sidebar.bone.level.accordion.toggle',
  visitSidebarPrintDownload: 'user.visit.sidebar.print.download',
  visitSidebarQRCode: 'user.visit.sidebar.qr.code',
  visitSidebarPatientEducationToggle:
    'user.visit.sidebar.patient.education.toggle',
  patientEducationTabChange: 'patient.education.tab.change',
  patientEducationTreatmentTabChange: 'patient.education.treatment.tab.change',
  imageViewerRedesignToggle: 'user.image.viewer.redesign.toggle',
  perioChartButtonClick: 'user.perio.chart.button.click',
  // Preview Dialog
  previewDialogPatientEducationToggle:
    'user.preview.dialog.patient.education.toggle',
  previewDialogFindingsToggle: 'user.preview.dialog.findings.toggle',
  previewDialogQRCodeGenerated: 'user.preview.dialog.qr.code.generated',
  qrCodeScanned: 'user.qr.code.scanned',
  previewDialogSelectTooth: 'user.preview.dialog.select.tooth',
  previewDialogDownload: 'user.preview.dialog.download',
  previewDialogPrint: 'user.preview.dialog.print',
  //--- Magic Links
  magicLinkHadSession: 'magic.link.had.session',
  magicLinkHadTokens: 'magic.link.had.tokens',
  magicLinkFailedToFetchTokens: 'magic.links.tokens.fetched.failed',
  magicLinkClosedSession: 'magic.link.closed.session',
  magicLinkRedirectToLogin: 'magic.link.redirect.to.login',
  //--- Page
  pageView: 'page.view',
  //--- Tableau
  tableauDashboardButtonClick: 'tableau.dashboard.button.click',
  tableauFilterChanged: 'tableau.filter.changed',
  tableauParameterChanged: 'tableau.parameter.changed',
  tableauTableRowClick: 'tableau.table.row.click',
  tableauVizLoad: 'tableau.viz.load',
  //--- Videa Notes
  videaNotesOpenSheet: 'view.notes',
  videaNotesStartRecorderClick: 'view.notes.recorder.start',
  videaNotesStartProviderOnlyRecorderClick:
    'view.notes.recorder.start.provider_only',
  videaNotesStartClose: 'view.notes.start.close',
  videaNotesGenerateNotesClick: 'view.notes.recorder.generate',
  videaNotesGenerateNotesError: 'view.notes.recorder.generate.error',
  videaNotesCancelRecorderClick: 'view.notes.recorder.cancel',
  videaNotesCancelRecorderConfirmationClick:
    'view.notes.recorder.cancel.confirmation',
  videaNotesPatientConsentDenied:
    'view.notes.recorder.cancel.patient_consent_denied',
  videaNotesPauseClick: 'view.notes.recorder.pause',
  videaNotesResumeClick: 'view.notes.recorder.resume',
  videaNotesRecorderClose: 'view.notes.recorder.close',
  videaNotesTextAreaClick: 'view.notes.result.textarea.click',
  videaNotesResultCopy: 'view.notes.result.copy',
  videaNotesResultLoadTime: 'view.notes.result.load.time',
  videaNotesS3UploadTime: 'view.notes.s3.upload.time',
  videaNotesResultClose: 'view.notes.result.close',
  videaNotesWritebackClick: 'view.notes.writeback.click',
  //--- Claims
  closeAttachmentEditor: 'user.close.attachment.editor',
  downloadAttachment: 'user.download.attachment',
  viewAttachment: 'user.view.attachment.editor',
  editClaimNarrative: 'user.edit.attachment.narrative',
  saveClaimNarrative: 'user.save.attachment.narrative',
  editClinicalNote: 'user.edit.attachment.clinicalnote',
  saveClinicalNote: 'user.save.attachment.clinicalnote',
  rateClaimAttachment: 'user.rating.attachment',
  //--- Claims: free version
  downloadFreeAttachment: 'user.download.free.attachment',
  viewFreeAttachment: 'user.view.free.attachment',
  //--- Insights
  insightsView: 'insights.view',
  insightsViewLoad: 'insights.view.load',
  insightsViewFilterChange: 'insights.view.filter.change',
  insightsViewSortChange: 'insights.view.sort.change',
  insightsFMXLinkClick: 'insights.fmx.link.click',
  insightsPinCreate: 'insights.pin.create',
  insightsPinDelete: 'insights.pin.delete',
  insightsMaterialityFilterChanged: 'insights.materiality.filter.changed',
  insightsPmsInfoSheetOpen: 'insights.pms.info.sheet.view',
} as const;

export type EventName = ValueOf<typeof mixpanelEvents>;

// useTrackEvent wraps useTrackEventWithOptionalParams and invokes useParams,
// which cannot be called outside of the React Router context.
// useTrackEventWithOptionalParams can be safely invoked outside of the React
// Router context (e.g., for Videa Notes).
export const useTrackEventWithOptionalParams = (params?: { id?: string }) => {
  const reduxUser = useSelector((state: RootStateOrAny) => state?.login?.user);

  const { mutate } = useEvents();
  const { oktaAuth } = useOktaAuth();
  const isEmbedded = useIsEmbedded();
  const isMagicLinkUser = useAtomValue(isMagicLinkUserAtom);
  const { data: preferences } = usePreferences();
  const shouldHideHuddle = useShouldHideHuddle();
  const huddleDate = useAtomValue(selectDateAtom);
  const estimatedVisitId = useAtomValue(selectedVisitAtom);
  const unifiedAppointmentId = useAtomValue(selectedUnifiedIdAtom);

  const trackEvent = useCallback(
    (
      eventName: EventName,
      payload?: Record<string, unknown>,
      options?: { transport?: 'xhr' | 'sendBeacon'; sendImmediately?: boolean },
      callback?: () => void,
      shouldDoubleWrite = true,
      skipMixpanelWrite = false,
      user = reduxUser,
    ) => {
      const payloadWithDefaults = {
        appVersion: process.env.VITE_VERSION,
        environment: process.env.VITE_ENV || 'development',
        user,
        organizationId: user?.primaryOrganizationId,
        organizationName: user?.primaryOrganizationName,
        practiceId: user?.primaryPracticeId,
        practiceName: user?.primaryPracticeName,
        userId: user?.id,
        username: user?.username,
        userRole: user?.role,
        applicationName: getAppName(user?.role),
        timestamp: Date.now(),
        isHybridUser: isEmbedded,
        isMagicLinkUser: isMagicLinkUser,
        configuration: user?.practice?.configuration,
        preferences,
        shouldHideHuddle,
        huddleDate: huddleDate?.toISOString(),
        patientId: params?.id,
        pmsType: user?.practice?.pmsType,
        estimatedVisitId: estimatedVisitId ? parseInt(estimatedVisitId) : null,
        unifiedAppointmentId: unifiedAppointmentId
          ? parseInt(unifiedAppointmentId)
          : null,
        ...payload,
      };

      if (!skipMixpanelWrite) {
        mixpanel.track(eventName, payloadWithDefaults, options, callback);
        Userpilot.track(eventName, payloadWithDefaults);
      }

      const jwtToken = getExternalJwtToken();
      if (
        !user?.primaryOrganizationId ||
        !user?.primaryPracticeId ||
        !shouldDoubleWrite ||
        (!oktaAuth.getAccessToken() && !jwtToken?.trim())
      ) {
        return;
      }

      mutate({ eventName, payload: payloadWithDefaults });
    },
    [
      reduxUser,
      isEmbedded,
      isMagicLinkUser,
      preferences,
      shouldHideHuddle,
      huddleDate,
      params?.id,
      estimatedVisitId,
      unifiedAppointmentId,
      oktaAuth,
      mutate,
    ],
  );

  return { trackEvent };
};

export const useTrackEvent = () => {
  const params = useParams<{ id: string }>();
  return useTrackEventWithOptionalParams(params);
};

export const useTrackImageView = () => {
  const { trackEvent } = useTrackEvent();
  const { id: patientId } = useParams<{ id: string }>();
  const { data: preferences } = usePreferences();
  const showSegmentation = useAtomValue(showSegmentationAtom);
  const sliderDisplayThreshold = useAtomValue(confidenceScoreFilterAtom);
  const isCasePresentationMode = useAtomValue(casePresentationModeAtom);
  const estimatedVisitId = useAtomValue(selectedVisitAtom);

  return {
    trackImageView: (
      image: CornerstoneImage,
      view?: Source,
      additionalData?: any,
      thumbnailPatientId?: string,
    ) =>
      trackEvent(mixpanelEvents.viewImage, {
        objectId: image.imageId,
        view: view || 'single-image-viewer',
        fileName: image.fileName,
        patientId:
          patientId || image.patientInfo?.patientId || thumbnailPatientId,
        detectedImageType: image.detectedImageType,
        patientViewToggle: showSegmentation,
        sliderDisplayThreshold,
        practiceDisplayThreshold: preferences?.confidenceThreshold,
        ...additionalData,
        casePresentation: isCasePresentationMode,
        estimatedVisitId: estimatedVisitId,
      }),
  };
};

/**
 * @deprecated prefer {@link useTrackEvent}
 */
export const trackEvent = async (eventName: any, user: any, payload = {}) => {
  const environment = process.env.VITE_ENV || 'development';

  let payloadWithEnvInfo = {
    ...payload,

    appVersion: process.env.VITE_VERSION,
    environment,
  };
  if (user) {
    payloadWithEnvInfo = {
      ...payloadWithEnvInfo,
      // @ts-expect-error TS(2322): Type '{ user: any; organizationId: any; organizati... Remove this comment to see the full error message
      user: user,
      organizationId: user.primaryOrganizationId,
      organizationName: user.primaryOrganizationName,
      practiceId: user.primaryPracticeId,
      practiceName: user.primaryPracticeName,
      userId: user.id,
      username: user.username,
    };
  }

  try {
    await mixpanel.track(eventName, payloadWithEnvInfo, () => {});
    Userpilot.track(eventName, payloadWithEnvInfo);
    if (!user.primaryPracticeId || !user.primaryOrganizationId) {
      return;
    }
    await makeAPIRequestReactQuery(
      `organizations/${user.primaryOrganizationId}/practices/${user.primaryPracticeId}/${endpoints.events}`,
      undefined,
      'POST',
      {
        data: {
          name: eventName,
          body: payloadWithEnvInfo,
        },
      },
    );
  } catch (err) {
    sendException(err, { tags: [['action', 'SEND_TRACKING_EVENT']] });
  }
};

export const formatUploadImageMixpanelEvent = (
  fileInfo: any,
  userInfo: any,
  isManualUpload: any,
) => {
  const { patientId, fileName } = fileInfo;
  const { username, primaryOrganizationId, primaryPracticeId } = userInfo;
  const uploadModality = isManualUpload ? 'manual' : 'automatic';
  return {
    patientId,
    fileName,
    username,
    primaryOrganizationId,
    primaryPracticeId,
    uploadModality,
    timestamp: Date.now(),
  };
};

export const formatAnalyzeImageMixpanelEvent = (
  fileInfo: any,
  aiAnalysis: any,
) => {
  const { patientId, fileName } = fileInfo;
  const {
    analysisState,
    videaErrorCode,
    detectedImageType,
    numberOfFindings,
    hasFindings,
  } = aiAnalysis;
  return {
    patientId,
    fileName,
    analysisState,
    videaErrorCode,
    detectedImageType,
    numberOfFindings,
    hasFindings,
  };
};
